import React, { useState, useRef, useEffect } from 'react';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled }) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [text]);

  // Auto-focus input when not disabled
  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-neutral-800 px-4 py-3">
      <div className="flex items-start gap-2">
        <span className={`text-sm leading-relaxed select-none mt-[3px] ${
          disabled ? 'text-neutral-600' : 'text-blue-500'
        }`}>&gt;</span>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => !disabled && setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? '...' : 'Message'}
          readOnly={disabled}
          rows={1}
          className={`flex-1 resize-none bg-transparent text-sm leading-relaxed
            text-neutral-100 placeholder:text-neutral-600
            focus:outline-none
            ${disabled ? 'opacity-40 cursor-default' : ''}`}
        />
      </div>
      <div className="flex justify-between items-center mt-2 text-[10px] text-neutral-700">
        <span>Enter to send, Shift+Enter for newline</span>
        <span>esc to close</span>
      </div>
    </div>
  );
};

export default ChatInput;
